import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

// Check if device is mobile
const isMobile = () => window.innerWidth <= 768;

const DraggableWindow = ({
  id,
  title,
  children,
  defaultPos,
  defaultSize = { width: 450, height: 400 },
  isOpen,
  isMinimized,
  onClose,
  onMinimize,
  onFocus,
  zIndex,
  Icon,
  glitchy = false
}) => {
  const [position, setPosition] = useState(() => {
    // On mobile, position doesn't matter as CSS handles fullscreen
    if (isMobile()) return { x: 0, y: 0 };

    const x = defaultPos.x === 'center'
      ? Math.max(50, (window.innerWidth - defaultSize.width) / 2)
      : defaultPos.x;
    const y = defaultPos.y === 'center'
      ? Math.max(50, (window.innerHeight - defaultSize.height) / 2)
      : defaultPos.y;
    return { x, y };
  });
  const [size, setSize] = useState(defaultSize);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isGlitching, setIsGlitching] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [mobile, setMobile] = useState(isMobile);
  const resizeRef = useRef(null);

  // Listen for resize to update mobile state
  useEffect(() => {
    const handleResize = () => setMobile(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!glitchy) return;

    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setIsGlitching(true);
        setTimeout(() => setIsGlitching(false), 200);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [glitchy]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(350, startWidth + (moveEvent.clientX - startX));
      const newHeight = Math.max(250, startHeight + (moveEvent.clientY - startY));
      setSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  if (!isOpen || isMinimized) return null;

  const windowClass = [
    'window',
    isMaximized && 'maximized',
    isGlitching && 'glitching',
    isResizing && 'resizing'
  ].filter(Boolean).join(' ');

  // On mobile, CSS handles all positioning/sizing
  const mobileStyles = mobile ? { zIndex } : {
    x: isMaximized ? 0 : position.x,
    y: isMaximized ? 0 : position.y,
    width: isMaximized ? '100%' : size.width,
    height: isMaximized ? 'auto' : size.height,
    zIndex,
    touchAction: 'none'
  };

  return (
    <motion.div
      className={windowClass}
      role="dialog"
      aria-label={`${title} window`}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      drag={!mobile && !isMaximized && !isResizing}
      dragMomentum={false}
      onMouseDown={() => onFocus(id)}
      style={mobileStyles}
      onDragEnd={(_, info) => {
        if (!mobile) {
          setPosition({ x: position.x + info.offset.x, y: position.y + info.offset.y });
        }
      }}
    >
      <div className="window-header">
        <div className="window-title">
          <span className="window-icon">
            {Icon && <Icon size={16} strokeWidth={1.5} />}
          </span>
          {title}
        </div>
        <div className="window-controls" role="group" aria-label="Window controls">
          {!mobile && <button className="win-btn minimize" onClick={() => onMinimize(id)} aria-label="Minimize window">─</button>}
          {!mobile && <button className="win-btn maximize" onClick={() => setIsMaximized(!isMaximized)} aria-label={isMaximized ? "Restore window" : "Maximize window"}>□</button>}
          <button className="win-btn close" onClick={() => onClose(id)} aria-label="Close window">×</button>
        </div>
      </div>
      <div className="window-content">
        {children}
      </div>
      {!isMaximized && !mobile && (
        <div
          className="resize-handle"
          onMouseDown={handleResizeStart}
          ref={resizeRef}
          aria-label="Resize window"
          style={{ touchAction: 'none' }}
        />
      )}
    </motion.div>
  );
};

export default DraggableWindow;
